import { styles } from './wdk-dialog-styles.js';
import wdkDialogBodyDefault from './wdk-dialog-body-default.js';
import wdkDialogConfirm from './wdk-dialog-confirm.js';
import wdkDialogBodyDestructive from './wdk-dialog-body-destructive';
import wdkDialogBodyFreestyle from './wdk-dialog-body-freestyle';
import wdkDialogTrigger from './wdk-dialog-trigger.js';
import { getUrlWithRemovedParam } from '../../Util.js';

customElements.define('wdk-dialog-body-default', wdkDialogBodyDefault);
customElements.define('wdk-dialog-body-destructive', wdkDialogBodyDestructive);
customElements.define('wdk-dialog-body-freestyle', wdkDialogBodyFreestyle);
customElements.define('wdk-dialog-confirm', wdkDialogConfirm);
customElements.define('wdk-dialog-trigger', wdkDialogTrigger);

export default class wdkDialog extends HTMLElement {
	async connectedCallback() {
		await new Promise(requestAnimationFrame); //wait a tick
		this.abortController = new AbortController();
		this.loadingSpinnterTimer = null;
		this.isNotInAppShell = document.querySelector('peg-app-shell') === null;

		if (this.getAttribute('open') !== null) {
			this.openModal({ preventOpenAnimation: true });
		}
	}

	async disconnectedCallback() {
		await new Promise(requestAnimationFrame); //wait a tick
		this.abortController.abort();
	}

	/**
	 * Opens the modal dialog with the specified options.
	 *
	 * @async
	 * @param {Object} [options={}] - The options for opening the modal.
	 * @param {string|null} [options.fetchUrl=null] - The URL to fetch content from.
	 * @param {boolean} [options.preventOpenAnimation=false] - Whether to prevent the open animation.
	 * @returns {Promise<void>} A promise that resolves when the modal is opened.
	 */
	async openModal(options) {
		// this.#moveModalboxDomToBottom();
		options = options || {};
		const fetchUrl = options.fetchUrl || null;
		const preventOpenAnimation = options.preventOpenAnimation || false;

		if (options.keepCurrentDialogOpen === undefined) {
			await this.#closeCurrentOpenDialog();
		}

		this.#appendStyleAndDialogTag();
		this.#deactivateAnimation(preventOpenAnimation);

		if (this.isNotInAppShell) {
			window.history.pushState({}, '', window.location.href + '#wdkDialogOpen');
		}

		this.setAttribute('open', '');
		await this.#getContent(fetchUrl);
		this.dialog.showModal();
		this.dialog.focus(); // Fix for Safari, wihout this the focus is on the button with the dotted outline visible

		this.#handleDefaultClickListener();
		this.#passAttributesToDialogBody();
		this.#handleBackdropVisibility('openDialog');
	}

	/*#moveModalboxDomToBottom() {
		//Maybe it woulf be good to have something like this, because now we stay at the DOM-Position that we added the Dialog to. This could have some pretty strong CSS-Styles leaking into our wdk-dialog-boy-* Components
		//When ein console.log here it seems that the openModal Function is completly worked thrhough before the disconnected is thrown, don't know if this could make problems
		const dialogPlaceholder = document.createElement('div');
		dialogPlaceholder.className = 'dialogPlaceholder';
		this.parentNode.insertBefore(dialogPlaceholder, this);
		document.body.appendChild(this);
	}*/

	/*#moveModalboxDomToOriginalPosition() {
		const placeholder = document.querySelector('.dialogPlaceholder');
		if (placeholder !== null) {
			placeholder.insertAdjacentElement('afterend', this);
			placeholder.remove();
		}
	}*/

	#appendStyleAndDialogTag() {
		const dialogElement = document.createElement('template');
		dialogElement.innerHTML = `
			${this.#sanitizeEvilHtml(styles)}
			<dialog></dialog>
		`;

		const clone = document.importNode(dialogElement.content, true);
		this.appendChild(clone);
		this.dialog = this.querySelector('dialog');

		this.#handleCloseEvents();
		this.#observeOpen();
	}

	#showLoadingSpinner() {
		//Doing this in 2 Steps prevents a little jumping in Chrome XL for the very first milliseconds...
		this.dialog.innerHTML = this.#sanitizeEvilHtml('<div class="wdk-loadingSpinner noDisplay is-loading t-white t-size200"></div>');
		this.loadingSpinnterTimer = window.setTimeout(() => {
			this.dialog.querySelector('.wdk-loadingSpinner')?.classList.remove('noDisplay');
		}, 1500);
	}

	#hideLoadingSpinner() {
		window.clearTimeout(this.loadingSpinnterTimer);
		this.dialog.querySelector('.wdk-loadingSpinner')?.remove();
	}

	async #getContent(fetchUrl = null) {
		const fetchContent = this.getAttribute('url') !== null || fetchUrl !== null;
		if (fetchContent) {
			try {
				this.#showLoadingSpinner();
				const url = this.getAttribute('url') || fetchUrl;
				const data = await fetchIt.get(url);
				this.dialog.innerHTML = this.#sanitizeEvilHtml(data);
				this.#createClosingX();
				this.dispatchEvent(new CustomEvent('wdk-dialog:fetch-success', { bubbles: true }));
			} catch (error) {
				await this.#handleFetchError(error);
			} finally {
				this.#hideLoadingSpinner();
			}
		} else {
			const innerTemplate = this.querySelector('template');
			const cloneInner = document.importNode(innerTemplate.content, true);
			this.dialog.appendChild(cloneInner);
			this.#createClosingX();
		}
	}

	#createClosingX() {
		if (this.getAttribute('closing-x') !== null) {
			const closeButtonLabel = this.#getLocalizedString('closeButtonLabel');
			const template = document.createElement('template');
			const closingXHtml = `
					<button type="button" class="closingX js-closeDialog" aria-label="${closeButtonLabel}">
						<svg viewBox="0 0 24 24">
						  <line x1="18" y1="6" x2="6" y2="18" stroke="black" stroke-width="1"/>
						  <line x1="6" y1="6" x2="18" y2="18" stroke="black" stroke-width="1"/>
						</svg>
					</button>
				`;
			template.innerHTML = this.#sanitizeEvilHtml(closingXHtml);
			const clone = document.importNode(template.content, true);
			this.dialog.prepend(clone);
		}
	}

	#deactivateAnimation(deactivate) {
		this.dialog.style.removeProperty('animation-name');
		this.dialog.style.removeProperty('transition');

		if (deactivate) {
			this.dialog.style.transition = 'none';
			this.dialog.style.animationName = 'none';
		}
	}

	/**
	 * @param {boolean} force
	 * */
	async closeModal(force = false, preventCloseAnimation = false) {
		await this.#closeAndCleanup({ force: force, preventAnimation: preventCloseAnimation, closedBy: 'programmatic' });
	}

	async #closeCurrentOpenDialog() {
		//Dialog opens when already a dialog is open
		const foundOpenDialog = document.querySelector('wdk-dialog dialog[open]'); //search on the whole Page. Check for really dialog-Tag is open
		if (foundOpenDialog !== null) {
			const currentWdkDialog = foundOpenDialog.closest('wdk-dialog[open]');
			currentWdkDialog.setAttribute('prevent-close-animation', '');
			await currentWdkDialog.closeModal(true);
		}
	}

	/**
	 * @param {TechnicalFetchCommandError} error - The error object.
	 * @returns {Promise<void>} A promise that resolves when #closeAndCleanup is done.
	 * @throws Will rethrow the error if it is not a technical error.
	 */
	async #handleFetchError(error) {
		if (error.type === 'technicalError') {
			await this.#closeAndCleanup({ closedBy: 'technicalError' });
			this.dispatchEvent(new CustomEvent('wdk-dialog:fetch-fail', { detail: { response: error } }));

			switch (error.message) {
				case 'browserTimeout':
					document.querySelector('wdk-snackbar#timeoutNotification').show();
					break;
				case 'sessionExpired':
					jPar.util.network.reloadForSessionExpired();
					break;
				case 'offline':
					document.dispatchEvent(new CustomEvent('serviceworker:triggerOfflineAttention', { bubbles: true }));
					break;
				default:
					if (document.querySelector('#errorDialog500') !== null) {
						await document.querySelector('#errorDialog500').openModal();
					} else {
						jPar.wdk.modalbox.show({ modalboxId: 'errorModalbox500' }); //Deprecated
					}
					console.error(error);
					break;
			}
		} else {
			throw error;
		}
	}

	preventClose() {
		this.isClosePrevented = true;
	}

	async #closeAndCleanup(config = {}) {
		const { force = false, preventAnimation = false, closedBy = 'unknown' } = config;

		this.#deactivateAnimation(preventAnimation);

		this.isClosePrevented = false;
		this.dispatchEvent(
			new CustomEvent('wdk-dialog:closing', {
				detail: { wdkDialog: this, isForceClose: force },
				bubbles: true,
			}),
		);

		if (force === true) {
			this.isClosePrevented = false;
		}

		if (this.isClosePrevented === false) {
			if (this.isNotInAppShell) {
				const newUrl = getUrlWithRemovedParam(document.location.href, 'wdkDialogOpen');
				history.replaceState(null, null, newUrl);
			}

			this.removeAttribute('open');
			this.#handleBackdropVisibility('closeDialog');
			this.dialog.close();
			await this.#animationsComplete();
			this.#resetContent();
			this.dispatchEvent(
				new CustomEvent('wdk-dialog:closed', {
					detail: { wdkDialog: this, isForceClose: force, closedBy: closedBy },
					bubbles: true,
				}),
			);
		}
	}

	#handleCloseEvents() {
		//TODO could be replaced with closedby https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/closedBy in the Future
		if (this.getAttribute('prevent-light-dismiss') === null) {
			//close on Background click
			this.dialog.addEventListener('click', async (event) => {
				if (event.target.nodeName === 'DIALOG') {
					await this.#closeAndCleanup({ closedBy: 'backgroundClick' });
				}
			});
			this.dialog.addEventListener('keydown', async (event) => {
				if (event.code === 'Escape') {
					await this.#closeAndCleanup({ closedBy: 'escapeKey' });
				}
			});

			//close on ESC -> Dialog has default cancel behavior on ESC so no need to handle it here
		} else {
			// prevent closing on ESC
			// there is a listener for "cancel" that can be prevented, but for some reason it works only for the first click. Second click will close the dialog
			this.dialog.addEventListener('keydown', (event) => {
				if (event.code === 'Escape') {
					event.preventDefault();
				}
			});
		}

		if (this.isNotInAppShell) {
			window.addEventListener(
				'popstate',
				async () => {
					// Close the modal if #hasOpenModal is not in the URL
					if (!window.location.hash.includes('wdkDialogOpen')) {
						await this.#closeAndCleanup({ closedBy: 'browserBack' });
					}
				},
				{ signal: this.abortController?.signal },
			);
		}
	}

	/**
	 * @param {string} action - The action to perform, either 'openDialog' or 'closeDialog'.
	 *                          - 'openDialog': Adds the current dialog to the list of opened dialogs and hides the backdrop for all other dialogs.
	 *                          - 'closeDialog': Removes the current dialog from the list of opened dialogs and restores the backdrop for the next dialog in the list.
	 */
	#handleBackdropVisibility(action) {
		const currentlyOpenedDialogList = jPar.state.wdkDialog.openedDialogList;

		if (action === 'openDialog') {
			if (!jPar.state.wdkDialog.openedDialogList.includes(this.id)) {
				jPar.state.wdkDialog.openedDialogList.unshift(this.id);
			}

			currentlyOpenedDialogList.slice(1).forEach((wdkDialogId) => {
				// we need to check the hole page for the opened dialogs
				document.querySelector(`#${wdkDialogId}`).setAttribute('no-backdrop', '');
			});
		} else if (action === 'closeDialog') {
			const index = jPar.state.wdkDialog.openedDialogList.indexOf(this.id);
			if (index > -1) {
				jPar.state.wdkDialog.openedDialogList.splice(index, 1);
			}

			// we need to check the hole page for the dialog with this id
			document.querySelector(`#${currentlyOpenedDialogList[0]}`)?.removeAttribute('no-backdrop');
		}
	}

	#passAttributesToDialogBody() {
		const wdkDialogBody = this.querySelector('wdk-dialog-body-default, wdk-dialog-body-destructive');
		if (wdkDialogBody !== null) {
			const attributesToPass = ['mobile-full-height', 'mobile-center', 'buttons-vertical', 'closing-x'];

			for (const attribute of this.attributes) {
				if (attributesToPass.includes(attribute.name)) {
					wdkDialogBody.setAttribute(attribute.name, attribute.value);
				}
			}
		}
	}

	#handleDefaultClickListener() {
		this.dialog.querySelectorAll('.js-closeDialog').forEach((elem) => {
			elem.addEventListener('click', async (event) => {
				if (event.currentTarget.classList.contains('closingX')) {
					await this.#closeAndCleanup({ closedBy: 'closingX' });
				} else {
					await this.#closeAndCleanup({ closedBy: 'closingButton' });
				}
			});
		});

		// Helper for Submitting the Form from outside the Form-Tag
		this.dialog.querySelector('[type="submit"]')?.addEventListener('click', (event) => {
			event.preventDefault();
			this.dialog.querySelector('form')?.requestSubmit();
		});
	}

	#sanitizeEvilHtml(string) {
		// Das muss nochmal ein richtiger sanatizer werden
		return String(string).replace(
			/<script\b|\bonerror=|\bonload=|\bonmouseover=|\bonmouseout=|\bonpagehide=|\bonpageshow=|\bonmessage=|\bonhashchange=|\bonbeforeunload=|\bonpopstate=|\bonresize=|\bonunload=|\bonkeydown=|\bonkeyup=|\bonkeypress=|\bonfocus=|\bonblur=|\bonchange=|\bonsubmit=|\bonreset=|\bonselect=|\bondblclick=|\bondrag=|\bondragend=|\bondragenter=|\bondragleave=|\bondragover=|\bondragstart=|\bondrop=|\bonwheel=|\boncopy=|\boncut=|\bonpaste=|\bonanimationend=|\bonanimationiteration=|\bonanimationstart=|\bontransitionend=|\bontouchcancel=|\bontouchend=|\bontouchmove=|\bontouchstart=|\bonclick=/gi,
			'',
		);
	}

	/**
	 * Wartet auf das Ende aller Animationen des Dialogs.
	 * @private
	 * @returns {Promise} Ein Promise, der nach n Sekunde erfüllt wird.
	 */
	#animationsComplete() {
		// const animations = this.getAnimations();
		// console.log('animations', animations);
		// const finishedAnimations = animations.map((animation) => animation.finished);
		// return Promise.allSettled(finishedAnimations);
		if (this.hasAttribute('prevent-open-animation') || this.hasAttribute('prevent-close-animation')) {
			return new Promise((resolve) => resolve());
		} else {
			return new Promise((resolve) => setTimeout(resolve, 500));
		}
	}

	#observeOpen() {
		const dialogOpenerObserver = new MutationObserver((mutations) => {
			mutations.forEach(async (mutation) => {
				if (mutation.attributeName === 'open') {
					const isOpen = this.dialog.hasAttribute('open');

					if (isOpen) {
						this.dispatchEvent(new CustomEvent('wdk-dialog:opening', { bubbles: true }));

						await this.#animationsComplete();
						this.dispatchEvent(new CustomEvent('wdk-dialog:opened', { detail: { wdkDialog: this }, bubbles: true }));
					}
				}
			});
		});

		dialogOpenerObserver.observe(this.dialog, { attributes: true });
	}

	#resetContent() {
		const children = Array.from(this.children);
		children.forEach((child) => {
			if (child.tagName.toLowerCase() !== 'template') {
				this.removeChild(child);
			}
		});

		this.removeAttribute('prevent-close-animation');
		this.removeAttribute('prevent-open-animation');
		// this.#moveModalboxDomToOriginalPosition();
	}

	#getLocalizedString(key) {
		const translations = {
			en: {
				closeButtonLabel: 'Close',
			},
			de: {
				closeButtonLabel: 'Schließen',
			},
			fr: {
				closeButtonLabel: 'Fermer',
			},
			es: {
				closeButtonLabel: 'Cerrar',
			},
			nl: {
				closeButtonLabel: 'Sluiten',
			},
			// Add more languages as needed
		};

		const userLanguages = navigator.languages;
		for (const language of userLanguages) {
			const langCode = language.split('-')[0]; // Extract the language code (e.g., 'en' from 'en-US')
			if (translations[langCode] && translations[langCode][key]) {
				return translations[langCode][key];
			}
		}

		return translations['en'][key]; // Fallback to English if no match is found
	}
}
